import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import jsPDF from 'jspdf';
import {
    DataGrid,
    GridColDef,
    GridFilterModel,
    GridFilterOperator,
    GridFilterInputValue,
    GridPaginationModel,
    GridSlots,
    GridSortModel,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    getGridNumericOperators,
    getGridStringOperators,
} from '@mui/x-data-grid';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import AddHomeIcon from '@mui/icons-material/AddHome';
import AddIcon from '@mui/icons-material/Add';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveIcon from '@mui/icons-material/Remove';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import UpgradeIcon from '@mui/icons-material/Upgrade';

import { GridColType } from '@mui/x-data-grid/models/colDef/gridColType';
import {
    Device,
    DeviceWithSticks,
    OnboardDeviceConnectionStatus,
    OnboardDevicesPagination,
    OnboardDevicesQueryParams,
    UserRoles,
} from '../interfaces';
import { transformKeyToTitle } from '../utils/transformKeyToTitle';
import { formatDate } from '../utils/formatDate';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    loadActivationSticks,
    loadOnboardDevices,
    loadOnboardDevicesHardwareConfigurations,
    loadOnboardDevicesOnlinePeers,
    loadSystemConfigurations,
    selectOnboardDevicesHardwareConfigurations,
    selectSelectedOnboardDeviceTableRows,
    setSelectedOnboardDeviceTableRows,
} from '../store/devices.reducer';
import { useCognitoUser } from '../hooks/useCognitoUser';
import { useManageGrid } from '../hooks/useManageGrid';
import { hasAllowedRole } from '../utils/hasAllowedRole';
import {
    ONBOARD_DEVICE_SERVER_SORT_FIELDS,
    ONBOARD_DEVICE_NUMERIC_FILTER_FIELDS,
    SCANNER_INPUT_RESET_TIMEOUT_MS,
    buildDeviceIdByMatrixCode,
    buildOnboardDeviceRows,
    buildOnboardDevicesFiltersFromGridFilterModel,
    filterDevicesByScannedIds,
    getOnboardDevicesSortQueryPatch,
    getSelectedRowsForDevices,
    isOnboardDeviceServerFilterable,
    isOnboardDeviceServerSortable,
} from '../utils/onboardDevicesTable';
import { ActivationSticksCell } from './GridCells/ActivationSticksCell';
import { ChecksCell } from './GridCells/ChecksCell';
import { CopyTextCell } from './GridCells/CopyTextCell';
import { DeviceSerialStatusCell } from './GridCells/DeviceSerialStatusCell';
import { OrganizationLabelCell } from './GridCells/OrganizationLabelCell';
import { UpToDateCell } from './GridCells/UpToDateCell';
import { VersionsCell } from './GridCells/VersionsCell';
import { AttachOrganizationDialog } from './AttachOrganizationDialog';
import { CreateDevicesDialog } from './CreateDevicesDialog';
import { GetSecretsDialog } from './GetSecretsDialog';
import { RemoveDevicesDialog } from './RemoveDevicesDialog';
import { UpdateVersionDialog } from './UpdateVersionDialog';
import { UploadOnboardDeviceLogsDialog } from './UploadOnboardDeviceLogsDialog';

interface OnboardDevicesTableGridProps {
    devices: DeviceWithSticks[];
    pagination: OnboardDevicesPagination;
    loading: boolean;
    error: string | null;
    query: OnboardDevicesQueryParams;
    onQueryChange: (queryPatch: Partial<OnboardDevicesQueryParams>) => void;
    onlineStatusByDeviceId?: Record<string, OnboardDeviceConnectionStatus>;
}

interface OnboardDevicesToolbarProps {
    devices: Device[];
    hasScannedFilter: boolean;
    scannedDevicesCount: number;
    onResetScannedDevices: () => void;
    onReloadOnboardDevices: () => void;
}

type OnboardDevicesDialog =
    | 'updateVersion'
    | 'getSecrets'
    | 'attachOrganization'
    | 'createDevices'
    | 'removeDevices'
    | 'uploadLogs';

const STRING_NOT_EQUALS_OPERATOR: GridFilterOperator = {
    label: 'not equal',
    value: 'notEquals',
    getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) {
            return null;
        }

        const filterItemValue = filterItem.value.trim();
        const collator = new Intl.Collator(undefined, {
            sensitivity: 'base',
            usage: 'search',
        });

        return (value) => {
            return value != null ? collator.compare(filterItemValue, value.toString()) !== 0 : true;
        };
    },
    InputComponent: GridFilterInputValue,
};

const NUMERIC_CONTAINS_OPERATOR: GridFilterOperator = {
    label: 'contains',
    value: 'contains',
    getApplyFilterFn: (filterItem) => {
        if (filterItem.value === undefined || filterItem.value === null || filterItem.value === '') {
            return null;
        }

        return (value) => {
            return value != null ? String(value).includes(String(filterItem.value)) : false;
        };
    },
    InputComponent: GridFilterInputValue,
    InputComponentProps: {
        type: 'number',
    },
};

const SERVER_STRING_FILTER_OPERATORS = [
    ...getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
    STRING_NOT_EQUALS_OPERATOR,
];
const SERVER_NUMERIC_FILTER_OPERATORS = [
    NUMERIC_CONTAINS_OPERATOR,
    ...getGridNumericOperators().filter((operator) => ['=', '!='].includes(operator.value)),
];

const isFormFieldElement = (target: EventTarget | null): target is HTMLElement => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const tagName = target.tagName;
    return target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
};

const exportToFile = (devices: Device[]) => {
    const formattedData = devices.map((device) => `${device.deviceSerial}\t${device.deviceId}`);
    const blob = new Blob([formattedData.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'Devices.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const transformToPdfTable = (devices: Device[], numColumns: number) => {
    const numRows = Math.ceil(devices.length / numColumns);
    const table: Array<Array<Device | null>> = [];

    for (let i = 0; i < numRows; i++) {
        const row: Array<Device | null> = devices.slice(i * numColumns, (i + 1) * numColumns);

        while (row.length < numColumns) {
            row.push(null);
        }

        table.push(row);
    }

    return table;
};

const exportToPdf = (devices: Device[]) => {
    const doc = new jsPDF();
    const columns = 6;
    const rowsPerPage = 15;
    const tableData = transformToPdfTable(devices, columns);
    const cellWidth = 31.7;
    const cellHeight = 17.5;
    let y = 23;
    let rowCount = 0;

    doc.setFontSize(11.8);
    doc.setDrawColor(200, 200, 200);

    tableData.forEach((row) => {
        if (rowCount >= rowsPerPage) {
            doc.addPage();
            doc.setFontSize(11.8);
            y = 23;
            rowCount = 0;
        }

        let x = 10;
        row.forEach((device) => {
            doc.rect(x, y - 8, cellWidth, cellHeight);

            if (device) {
                const text = `${device.deviceId}`;
                const textWidth = doc.getTextWidth(text);
                doc.text(text, x + cellWidth - textWidth - 3, y + 5.5);
            }

            x += cellWidth;
        });

        y += cellHeight;
        rowCount++;
    });

    doc.save('Devices.pdf');
};

const getSelectedDevices = (selectedRows: string[], devices: Device[]) => {
    return selectedRows
        .map((id) => devices.find((device) => device.deviceId === id))
        .filter((device): device is Device => Boolean(device));
};

function OnboardDevicesToolbar({
    devices,
    hasScannedFilter,
    scannedDevicesCount,
    onResetScannedDevices,
    onReloadOnboardDevices,
}: OnboardDevicesToolbarProps) {
    const dispatch = useAppDispatch();
    const { userRoles } = useCognitoUser();
    const selectedRows = useAppSelector(selectSelectedOnboardDeviceTableRows);
    const [activeDialog, setActiveDialog] = useState<OnboardDevicesDialog | null>(null);

    const isAdmin = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, [UserRoles.ADMINS]);
    }, [userRoles]);

    const onReload = () => {
        onReloadOnboardDevices();
        dispatch(loadActivationSticks());
        dispatch(loadSystemConfigurations());
        dispatch(loadOnboardDevicesHardwareConfigurations());
        dispatch(loadOnboardDevicesOnlinePeers());
    };

    const closeDialog = () => setActiveDialog(null);

    const closeDialogAndReload = () => {
        closeDialog();
        onReloadOnboardDevices();
    };

    const onExportIds = () => {
        const selectedDevices = getSelectedDevices(selectedRows, devices);

        exportToFile(selectedDevices);
        exportToPdf(selectedDevices);
    };

    return (
        <>
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <Button
                    startIcon={<FilterAltOffIcon />}
                    disabled={!hasScannedFilter}
                    color="primary"
                    onClick={onResetScannedDevices}
                >
                    {hasScannedFilter
                        ? `Clear Scanned Devices (${scannedDevicesCount})`
                        : 'Clear Scanned Devices'}
                </Button>
                {isAdmin && (
                    <Button
                        startIcon={<UpgradeIcon />}
                        disabled={selectedRows.length === 0}
                        color="primary"
                        onClick={() => setActiveDialog('updateVersion')}
                    >
                        Change Version
                    </Button>
                )}
                <Button startIcon={<RefreshIcon />} color="primary" onClick={onReload}>
                    Reload
                </Button>
                <Button
                    disabled={selectedRows.length !== 1}
                    startIcon={<KeyIcon />}
                    color="primary"
                    onClick={() => setActiveDialog('getSecrets')}
                >
                    Get Secrets
                </Button>
                {isAdmin && (
                    <Button
                        disabled={selectedRows.length === 0}
                        startIcon={<AddHomeIcon />}
                        color="primary"
                        onClick={() => setActiveDialog('attachOrganization')}
                    >
                        Attach Organization
                    </Button>
                )}
                <Button
                    startIcon={<AddIcon />}
                    color="primary"
                    onClick={() => setActiveDialog('createDevices')}
                >
                    Create Devices
                </Button>
                <Button
                    disabled={selectedRows.length === 0}
                    startIcon={<RemoveIcon />}
                    color="primary"
                    onClick={() => setActiveDialog('removeDevices')}
                >
                    Remove Devices
                </Button>
                <Button
                    startIcon={<AddIcon />}
                    color="primary"
                    disabled={selectedRows.length === 0}
                    onClick={onExportIds}
                >
                    Export Ids
                </Button>
                <Button
                    startIcon={<StickyNote2Icon />}
                    color="primary"
                    disabled={selectedRows.length !== 1}
                    onClick={() => setActiveDialog('uploadLogs')}
                >
                    Upload Logs
                </Button>
            </GridToolbarContainer>

            {activeDialog === 'updateVersion' && (
                <UpdateVersionDialog
                    selectedDeviceIds={selectedRows}
                    open
                    onClose={closeDialog}
                    onUpdateComplete={closeDialogAndReload}
                />
            )}
            {activeDialog === 'getSecrets' && (
                <GetSecretsDialog selectedDeviceId={selectedRows[0]} open onClose={closeDialog} />
            )}
            {activeDialog === 'attachOrganization' && (
                <AttachOrganizationDialog
                    selectedDeviceIds={selectedRows}
                    open
                    onClose={closeDialog}
                    onAttachComplete={closeDialogAndReload}
                />
            )}
            {activeDialog === 'createDevices' && (
                <CreateDevicesDialog open onClose={closeDialog} onCreationCompleted={closeDialogAndReload} />
            )}
            {activeDialog === 'removeDevices' && (
                <RemoveDevicesDialog
                    open
                    selectedDeviceIds={selectedRows}
                    onClose={closeDialog}
                    onRemovalCompleted={closeDialogAndReload}
                />
            )}
            {activeDialog === 'uploadLogs' && (
                <UploadOnboardDeviceLogsDialog
                    selectedDeviceId={selectedRows[0]}
                    open
                    onClose={closeDialog}
                />
            )}
        </>
    );
}

const ONBOARD_DEVICE_COLUMN_DEFS = [
    { key: 'deviceSerial', type: 'number', renderCell: DeviceSerialStatusCell },
    { key: 'deviceId', renderCell: CopyTextCell },
    { key: 'organization', renderCell: OrganizationLabelCell },
    { key: 'status', renderCell: CopyTextCell },
    { key: 'vpnOnlineAt', headerName: 'Last Online', valueFormatter: formatDate },
    { key: 'systemVersion', renderCell: VersionsCell },
    { key: 'hardwareConfiguration', renderCell: CopyTextCell },
    { key: 'upToDate', renderCell: UpToDateCell },
    { key: 'checks', renderCell: ChecksCell },
    { key: 'logsS3Link', renderCell: CopyTextCell },
    { key: 'createdAt', valueFormatter: formatDate },
    { key: 'updatedAt', valueFormatter: formatDate },
    { key: 'activationSticksCount', type: 'number', renderCell: ActivationSticksCell },
    { key: 'baseImageS3Link', renderCell: CopyTextCell },
    { key: 'commitHash', renderCell: CopyTextCell },
    { key: 'zymkeySerial', renderCell: CopyTextCell },
    { key: 'rpiSerial', renderCell: CopyTextCell },
    { key: 'username', renderCell: CopyTextCell },
    { key: 'hostname', renderCell: CopyTextCell },
    { key: 'sshPort', type: 'number', renderCell: CopyTextCell },
    { key: 'accessPointSSID', renderCell: CopyTextCell },
    { key: 'vpnProfile', renderCell: CopyTextCell },
    { key: 'vpnIPAddress', renderCell: CopyTextCell },
    { key: 'vpnPubkey', renderCell: CopyTextCell },
    { key: 'bundleDevicePubkey', renderCell: CopyTextCell },
    { key: 'bundleCloudPubkey', renderCell: CopyTextCell },
    { key: 'mapS3Link', renderCell: CopyTextCell },
    { key: 'zymkeyPubkey', renderCell: CopyTextCell },
    { key: 'deviceSSHPubkey', renderCell: CopyTextCell },
    { key: 'deviceSSHPubkeyType', renderCell: CopyTextCell },
    { key: 'deviceSSHPubkeyHash', renderCell: CopyTextCell },
];

export const OnboardDevicesTableGrid = ({
    devices,
    pagination,
    loading,
    error,
    query,
    onQueryChange,
    onlineStatusByDeviceId,
}: OnboardDevicesTableGridProps) => {
    const dispatch = useAppDispatch();
    const {
        sortModel,
        columnsConfig,
        columnsWidth,
        gridFilterModel,
        handleColumnVisibilityChange,
        handleColumnWidthChange,
        handleSortModelChange,
        handleFilterModelChange,
        onCellKeyDown,
        gridStateSynced,
    } = useManageGrid('devices/onboard');

    const selectedRows = useAppSelector(selectSelectedOnboardDeviceTableRows);
    const hardwareConfigurations = useAppSelector(selectOnboardDevicesHardwareConfigurations);
    const [scannedDeviceIds, setScannedDeviceIds] = useState<string[]>([]);
    const scannerBufferRef = useRef('');
    const lastScannerInputAtRef = useRef<number | null>(null);
    const gridStateAppliedRef = useRef(false);

    useEffect(() => {
        dispatch(loadOnboardDevicesHardwareConfigurations());
    }, [dispatch]);

    const sanitizedSortModel = useMemo<GridSortModel>(() => {
        const persistedSortModel = (sortModel || []) as GridSortModel;

        return persistedSortModel.filter((item) => ONBOARD_DEVICE_SERVER_SORT_FIELDS.has(item.field));
    }, [sortModel]);

    const hardwareConfigurationNameById = useMemo(() => {
        const map = new Map<string, string>();
        (hardwareConfigurations ?? []).forEach((configuration) =>
            map.set(configuration.id!, configuration.name),
        );

        return map;
    }, [hardwareConfigurations]);

    const visibleDevices = useMemo(() => {
        return filterDevicesByScannedIds(devices, scannedDeviceIds);
    }, [devices, scannedDeviceIds]);

    const rows = useMemo(() => {
        return buildOnboardDeviceRows(visibleDevices, hardwareConfigurationNameById, onlineStatusByDeviceId);
    }, [hardwareConfigurationNameById, onlineStatusByDeviceId, visibleDevices]);

    const columns: GridColDef[] = useMemo(() => {
        return ONBOARD_DEVICE_COLUMN_DEFS.map((col) => {
            const isServerFilterable = isOnboardDeviceServerFilterable(col.key);

            return {
                field: col.key,
                type: (col.type || 'string') as GridColType,
                headerName: col.headerName || transformKeyToTitle(col.key),
                renderCell: col.renderCell,
                valueFormatter: col.valueFormatter,
                width: columnsWidth[col.key] || 200,
                align: 'left',
                headerAlign: 'left',
                sortable: isOnboardDeviceServerSortable(col.key),
                filterable: isServerFilterable,
                filterOperators: isServerFilterable
                    ? ONBOARD_DEVICE_NUMERIC_FILTER_FIELDS.has(col.key)
                        ? SERVER_NUMERIC_FILTER_OPERATORS
                        : SERVER_STRING_FILTER_OPERATORS
                    : undefined,
            };
        });
    }, [columnsWidth]);

    const onReloadOnboardDevices = useCallback(() => {
        dispatch(loadOnboardDevices(query));
    }, [dispatch, query]);

    const handlePaginationModelChange = useCallback(
        (model: GridPaginationModel) => {
            onQueryChange({
                page: model.page + 1,
                pageSize: model.pageSize,
            });
        },
        [onQueryChange],
    );

    const handleServerSortModelChange = useCallback(
        (model: GridSortModel) => {
            const nextSortModel = model.filter((item) => ONBOARD_DEVICE_SERVER_SORT_FIELDS.has(item.field));

            handleSortModelChange(nextSortModel);
            onQueryChange({
                page: 1,
                ...getOnboardDevicesSortQueryPatch(nextSortModel),
            });
        },
        [handleSortModelChange, onQueryChange],
    );

    const handleServerFilterModelChange = useCallback(
        (model: GridFilterModel) => {
            handleFilterModelChange(model);
            onQueryChange({
                page: 1,
                filters: buildOnboardDevicesFiltersFromGridFilterModel(model),
            });
        },
        [handleFilterModelChange, onQueryChange],
    );

    const applyMatrixCodeFilter = useCallback(
        (matrixCode: string) => {
            const persistedFilterItems = (gridFilterModel?.items ?? []) as GridFilterModel['items'];
            const nextFilterModel: GridFilterModel = {
                ...gridFilterModel,
                items: [
                    ...persistedFilterItems.filter((item) => item.field !== 'matrixCode'),
                    {
                        id: 'scanner-matrix-code',
                        field: 'matrixCode',
                        operator: 'equals',
                        value: matrixCode,
                    },
                ],
            };

            handleServerFilterModelChange(nextFilterModel);
        },
        [gridFilterModel, handleServerFilterModelChange],
    );

    useEffect(() => {
        if (!gridStateSynced || gridStateAppliedRef.current) {
            return;
        }

        gridStateAppliedRef.current = true;

        onQueryChange({
            page: 1,
            ...getOnboardDevicesSortQueryPatch(sanitizedSortModel),
            filters: buildOnboardDevicesFiltersFromGridFilterModel(gridFilterModel),
        });
    }, [gridFilterModel, gridStateSynced, onQueryChange, sanitizedSortModel]);

    useEffect(() => {
        const nextSelectedRows = getSelectedRowsForDevices(selectedRows, visibleDevices);

        if (nextSelectedRows.length !== selectedRows.length) {
            dispatch(setSelectedOnboardDeviceTableRows(nextSelectedRows));
        }
    }, [dispatch, selectedRows, visibleDevices]);

    useEffect(() => {
        const deviceIdByMatrixCode = buildDeviceIdByMatrixCode(devices);

        const onWindowKeyDown = (event: KeyboardEvent) => {
            if (isFormFieldElement(event.target)) {
                return;
            }

            if (event.key === 'Enter') {
                const scannedCode = scannerBufferRef.current.trim();
                scannerBufferRef.current = '';
                lastScannerInputAtRef.current = null;

                if (!scannedCode) {
                    return;
                }

                const matchedDeviceId = deviceIdByMatrixCode.get(scannedCode.toUpperCase());
                if (!matchedDeviceId) {
                    applyMatrixCodeFilter(scannedCode);
                    return;
                }

                setScannedDeviceIds((previousIds) => {
                    return previousIds.includes(matchedDeviceId)
                        ? previousIds
                        : [...previousIds, matchedDeviceId];
                });

                return;
            }

            if (event.key === 'Escape') {
                scannerBufferRef.current = '';
                lastScannerInputAtRef.current = null;
                return;
            }

            if (event.key === 'Backspace') {
                scannerBufferRef.current = scannerBufferRef.current.slice(0, -1);
                lastScannerInputAtRef.current = Date.now();
                return;
            }

            if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
                return;
            }

            const now = Date.now();
            if (
                lastScannerInputAtRef.current !== null &&
                now - lastScannerInputAtRef.current > SCANNER_INPUT_RESET_TIMEOUT_MS
            ) {
                scannerBufferRef.current = '';
            }

            scannerBufferRef.current += event.key;
            lastScannerInputAtRef.current = now;
        };

        window.addEventListener('keydown', onWindowKeyDown);
        return () => window.removeEventListener('keydown', onWindowKeyDown);
    }, [applyMatrixCodeFilter, devices]);

    const onResetScannedDevices = useCallback(() => {
        setScannedDeviceIds([]);
    }, []);

    const onRowSelectionModelChange = (rows: any[]) => {
        dispatch(setSelectedOnboardDeviceTableRows(rows));
    };

    const Toolbar = useCallback(
        () => (
            <OnboardDevicesToolbar
                devices={devices}
                hasScannedFilter={scannedDeviceIds.length > 0}
                scannedDevicesCount={scannedDeviceIds.length}
                onResetScannedDevices={onResetScannedDevices}
                onReloadOnboardDevices={onReloadOnboardDevices}
            />
        ),
        [devices, onReloadOnboardDevices, onResetScannedDevices, scannedDeviceIds.length],
    );

    return (
        <>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {gridStateSynced && (
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    rowCount={scannedDeviceIds.length > 0 ? rows.length : pagination.totalItems}
                    paginationModel={{
                        page: Math.max(query.page - 1, 0),
                        pageSize: query.pageSize,
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    onPaginationModelChange={handlePaginationModelChange}
                    slots={{
                        toolbar: Toolbar as GridSlots['toolbar'],
                    }}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    rowSelectionModel={selectedRows}
                    checkboxSelection
                    disableRowSelectionOnClick
                    onCellKeyDown={onCellKeyDown}
                    filterModel={gridFilterModel}
                    sortModel={sanitizedSortModel}
                    onSortModelChange={handleServerSortModelChange}
                    onColumnWidthChange={handleColumnWidthChange}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    onFilterModelChange={handleServerFilterModelChange}
                    columnVisibilityModel={columnsConfig}
                />
            )}
        </>
    );
};
